import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { getThLegacyCognitoIdFromClaims } from 'microsites/partner-linking/app-token';

import { userHasSignedIn as userHasSignedInOnCognito } from 'remote/auth';
import { IAdobeEvent, TGTMDataLayer } from 'state/adobe/adobe-analytics-adapter/types';
import { initDatadogFlags } from 'state/datadog-flags';
import { legacyFlags } from 'state/datadog-flags/legacy-flags';
import { initCognitoSession } from 'utils/cognito';
import { init as initDataDogLogs } from 'utils/datadog';
import { brand, isLocalDev, platform } from 'utils/environment';
import LaunchDarklyHelper, { LaunchDarklyFlag } from 'utils/launchdarkly';

declare global {
  interface Window {
    dataLayer: TGTMDataLayer;
    adobeDataLayer: IAdobeEvent<Record<string, any>>[];
    LOADING_START_TIME: number;
    /**
     * window.Cypress exists when our app is running within Cypress
     * Places we currently check for this:
     * - frontend/src/state/graphql/links/index.ts - avoid BatchHttpLink
     *
     * https://docs.cypress.io/faq/questions/using-cypress-faq.html#Is-there-any-way-to-detect-if-my-app-is-running-under-Cypress
     */
    Cypress?: any;
    // When run with cypress for cypress-v2 package,
    // test harness application defaults (not provider snapshots)
    _initial_cypress_feature_flags?: object;
    // Skip the interval for checking for unavailable items.
    // Avoids race condition in cart with recorded tests
    _skipUnavailableItemsIntervalCheck?: boolean;
  }
}

const emitLoadingSuccessEvent = (rbiBrand: string, rbiPlatform: string) => {
  const successTime = performance.now?.();
  const durationTillSuccess = successTime - window.LOADING_START_TIME;
  (window.dataLayer || []).push({
    event: 'APP_BUNDLE_LOADING_SUCCESS',
    brand: rbiBrand,
    timestamp: successTime,
    duration: durationTillSuccess,
    platform: rbiPlatform,
  });
};

export default async function bootstrap(
  renderApp: ({
    flags,
    userHasSignedIn,
    userSession,
  }: {
    flags: Record<string, unknown>;
    userHasSignedIn: boolean;
    userSession: CognitoUserSession | null;
  }) => void
): Promise<void> {
  // Ensure dataLayer exists so events logged before GTM injection are preserved.
  window.dataLayer = window.dataLayer || [];

  const [BRAND, PLATFORM] = [brand().toUpperCase(), platform()];

  if (!isLocalDev) {
    emitLoadingSuccessEvent(BRAND, PLATFORM);
  }

  const [helper, userSession] = await Promise.all([
    LaunchDarklyHelper.init(),
    initCognitoSession(),
  ]);
  const claims = userSession?.getIdToken?.()?.payload;
  if (claims?.sub) {
    await helper.updateCurrentUser({
      key: claims.sub,
      anonymous: false,
      custom: { cognitoId: claims.sub },
    });
  } else if (!helper.datadogUserAttributes.anonymous) {
    await helper.clearCurrentUser();
  }
  // The named legacy domain retains original account/anonymous targeting;
  // the existing newer DD domain keeps its loyalty/browser targeting.
  await Promise.all([
    legacyFlags.initialize(helper.datadogUserAttributes),
    initDatadogFlags({
      cognitoId: claims?.sub,
      legacyCognitoId: getThLegacyCognitoIdFromClaims(claims),
    }),
  ]);
  initDataDogLogs(legacyFlags.read(LaunchDarklyFlag.DATADOG_LOG_SAMPLE_RATE, undefined));

  // this has to be after init cognito session.
  const userHasSignedIn = await userHasSignedInOnCognito();

  renderApp({
    flags: {},
    userHasSignedIn,
    userSession,
  });
}
